/**
 * The board itself — one column per client, in `tasks_summary`'s own order.
 *
 * The columns are the read model's, not this page's: `readmodels/tasks-board.ts`
 * sorts by company name and puts the studio's own column last, and a board that
 * re-sorted them would be a second answer to a settled question.
 *
 * `--board-cols` is set by the `Board` primitive from `cols`, which is CONTRACT.md
 * §4.1's third and last sanctioned inline value. Nothing here writes it.
 *
 * The column with `client === null` is not a company: its head says so in muted
 * copy, and its cards are grouped by queue. Client columns stay flat — a swimlane
 * there is a different decision.
 *
 * Each card carries the same `RowActions` as a list row (#5) — Claim / Start / …
 * plus the overflow menu — so the board is not read-only triage.
 */

import type { TaskView } from '@fareast/shared'

import { Board, BoardCol, BoardHead, BoardWrap } from '../../../components/portal/Board.js'
import { Button } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Stack } from '../../../components/portal/Layout.js'
import { Notice, Pill } from '../../../components/portal/Status.js'
import { Muted, Small, Xs } from '../../../components/portal/Text.js'
import { isRestrictedStaff, usePrincipal } from '../../../auth/principal.js'
import { ClientLink } from '../../../components/studio/ClientLink.js'
import { TaskCardFull } from '../../../components/studio/TaskCardFull.js'
import { useFormat } from '../../../format/useFormat.js'
import {
  BOARD_MAX_COLUMNS,
  columnInput,
  groupByQueue,
  useTaskPages,
  type BoardColumn,
  type TaskFilterInput,
  type TasksSummary,
} from './board.js'
import { useFieldSave, type FieldSave } from './fieldSave.js'
import { RowActions } from './RowActions.js'
import { LoadMore } from './TaskList.js'

export type TaskBoardProps = {
  /** One column per client the summary names (`board.ts` `columnsOf`). */
  readonly columns: readonly BoardColumn[]
  /** The page's filters, which every column's own read narrows to its client. */
  readonly filters: TaskFilterInput
  /** The summary's clients past `max_columns`, or null when there are none. */
  readonly others: TasksSummary['others']
  /** `service_code` → the catalog's own name for it. */
  readonly serviceName: (code: string | null) => string | undefined
  /** Shows the same filters as a list, which is where the `others` are. */
  readonly onShowList: () => void
  /** The instant every card measures "late" against. One clock per render. */
  readonly at: Date
}

export function TaskBoard({
  columns,
  filters,
  others,
  serviceName,
  onShowList,
  at,
}: TaskBoardProps) {
  const clientMuted = isRestrictedStaff(usePrincipal())
  const format = useFormat()
  /*
   * One for the whole board, handed down. `useFieldSave` holds two mutations, and a
   * board is thirty cards — calling it inside the card would be sixty hooks to write
   * one sentence, and the sentence is the page's single line anyway.
   */
  const field = useFieldSave()
  /*
   * The clients the summary left out, or null when it left none. `others` is answered
   * whenever `max_columns` is sent, which the page always does, and when every client
   * fitted it is there with nothing in it rather than null.
   */
  const leftOut = others !== null && others.clients > 0 ? others : null

  if (columns.length === 0) {
    return <Empty>Nothing is in hand for anybody under this filter.</Empty>
  }

  return (
    <>
      {leftOut === null ? null : (
        <TooManyColumns
          columns={columns.filter((one) => one.client !== null).length + leftOut.clients}
        />
      )}

      <BoardWrap>
        <Board cols={columns.length}>
          {columns.map((column) => (
            <BoardCol key={column.key}>
              <ColumnHead column={column} clientMuted={clientMuted} />
              <ColumnBody
                column={column}
                filters={filters}
                serviceName={serviceName}
                at={at}
                field={field}
              />
            </BoardCol>
          ))}
        </Board>
      </BoardWrap>

      {/*
        The clients past the twelve columns (`max_columns`), as the summary counts
        them. The board draws none of their cards; the list holds every one of them
        under the same filters, one table sorted by due date.
      */}
      {leftOut === null ? null : (
        <div>
          <Small>
            <Muted>
              {format.number(leftOut.clients)} more {leftOut.clients === 1 ? 'client' : 'clients'}{' '}
              with {format.number(leftOut.matching)} {leftOut.matching === 1 ? 'task' : 'tasks'}{' '}
              under these filters.{' '}
            </Muted>
          </Small>
          <Button size="sm" variant="ghost" onClick={onShowList}>
            Show them in the list
          </Button>
        </div>
      )}
    </>
  )
}

/**
 * One column's cards: its own `tasks_list`, the page's filters narrowed to the
 * column's client, a page at a time with its own Load more (Roberto, 21 Sep). The
 * head's number is the summary's; this draws what has been loaded of it.
 */
function ColumnBody({
  column,
  filters,
  serviceName,
  at,
  field,
}: {
  readonly column: BoardColumn
  readonly filters: TaskFilterInput
  readonly serviceName: (code: string | null) => string | undefined
  readonly at: Date
  readonly field: FieldSave
}) {
  const pages = useTaskPages(columnInput(filters, column))
  if (pages.isPending) {
    return (
      <Small>
        <Muted>Reading…</Muted>
      </Small>
    )
  }
  return (
    <>
      {column.client === null ? (
        <StudioColumnBody tasks={pages.rows} serviceName={serviceName} at={at} field={field} />
      ) : (
        pages.rows.map((task) => (
          <BoardCard
            key={task.ref}
            task={task}
            serviceName={serviceName(task.service_code)}
            at={at}
            field={field}
          />
        ))
      )}
      <LoadMore pages={pages} onLoadMore={pages.loadMore} what={`tasks for ${column.name}`} />
    </>
  )
}

function ColumnHead({
  column,
  clientMuted,
}: {
  readonly column: BoardColumn
  readonly clientMuted: boolean
}) {
  return (
    <>
      <BoardHead count={column.count}>
        {column.client === null ? (
          column.name
        ) : (
          <ClientLink client={column.client} name={column.name} muted={clientMuted} />
        )}
        {column.pastDue === true ? <Pill state="bad">Past due</Pill> : null}
      </BoardHead>
      {column.client === null ? (
        <div>
          <Small>
            <Muted>Internal studio work</Muted>
          </Small>
        </div>
      ) : null}
    </>
  )
}

/** Queue groups for the studio column only — same `QUEUE_WORD` labels as the card. */
function StudioColumnBody({
  tasks,
  serviceName,
  at,
  field,
}: {
  readonly tasks: readonly TaskView[]
  readonly serviceName: (code: string | null) => string | undefined
  readonly at: Date
  readonly field: FieldSave
}) {
  return (
    <Stack gap="tight">
      {groupByQueue(tasks).map((group) => (
        <div key={group.queue}>
          <Xs block>
            <Muted>{group.label}</Muted>
          </Xs>
          {group.tasks.map((task) => (
            <BoardCard
              key={task.ref}
              task={task}
              serviceName={serviceName(task.service_code)}
              at={at}
              field={field}
            />
          ))}
        </div>
      ))}
    </Stack>
  )
}

/**
 * One card, wired to the page's own line.
 *
 * The card's date is edited in the cell exactly as the list's is, so it owes the same
 * sentence and the same Undo — and, when the write is refused, the same refusal.
 * Before this the board's two `TaskCardFull` call sites handed `CellDue` neither, and
 * `CellDue` calls both through `?.`, so a board card that could not save said nothing
 * at all. `useFieldSave` is the one place those words live (`fieldSave.ts`), and it is
 * handed in from `TaskBoard`, which holds one for the whole board.
 */
function BoardCard({
  task,
  serviceName,
  at,
  field,
}: {
  readonly task: TaskView
  readonly serviceName: string | undefined
  readonly at: Date
  readonly field: FieldSave
}) {
  return (
    <TaskCardFull
      task={task}
      serviceName={serviceName}
      at={at}
      actions={<RowActions task={task} />}
      onDateSaved={(previous, next, since) => {
        field.saved({ kind: 'due', taskRef: task.ref, since, previous, next })
      }}
      onDateError={field.failed}
    />
  )
}

/**
 * `.docs/20` §Edge cases: "Board with more than 12 clients → the list view is the
 * default and the board warns."
 *
 * The default is `viewFor` in `board.ts`; this is the warning, and it is shown only
 * to somebody who asked for the board anyway. Ochre because the next move is theirs
 * — P4: this needs your decision.
 *
 * Since session 42 the summary itself stops at `BOARD_MAX_COLUMNS` columns and counts
 * the rest in `others`, so the board draws twelve and says how many it left out.
 */
function TooManyColumns({ columns }: { readonly columns: number }) {
  return (
    <Notice need role="status">
      {String(columns)} clients have work under these filters, and the board draws{' '}
      {String(BOARD_MAX_COLUMNS)} of them: the ones with the most overdue and then the most open
      work. The rest are counted below the board, and the list shows every task in one table.
    </Notice>
  )
}
